import { Box, Grid, GridItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Layout } from "../../../../common/layout/Layout";
import { EventHeader } from "./layout/EventHeader";
import { MobileStepDrawer } from "./layout/MobileStepDrawer";
import { PublicEventNavigation } from "./layout/PublicEventNavigation";
import { InfoStep } from "./steps/InfoStep";
import { ProcedureStep } from "./steps/ProcedureStep";
import { QualificationStep } from "./steps/QualificationStep";
import { SummaryStep } from "./steps/SummaryStep";
import { type SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { CONTENT_WIDTH } from "../../../../config";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
}

export const EditPublicSourcingEventForm = ({ eventId, event }: Props) => {
  const { t } = useTranslation();
  const { currentStep, navigateToInfo, navigateToProcedure, navigateToQualification, navigateToSummary } = useStepNavigation(eventId);

  if (!currentStep) return <div>{t("Loading")}</div>;

  return (
    <Layout>
      <MobileStepDrawer
        navigateToInfo={navigateToInfo}
        navigateToProcedure={navigateToProcedure}
        navigateToQualification={navigateToQualification}
        navigateToSummary={navigateToSummary}
      />
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows="auto minmax(min-content, auto)"
        templateAreas={`"contentLeft content contentRight"`}
        width="full"
        maxWidth="full"
        overflowX="hidden"
      >
        <GridItem area="content" maxWidth="full" width="full">
          <Grid
            gridTemplateColumns={{ base: "1fr", md: "260px 1fr" }}
            gridTemplateRows="70px minmax(min-content, auto)"
            templateAreas={{
              base: `"contentNav" "content"`,
              md: `"menuNav contentNav" "menu content"`,
            }}
            columnGap={{ base: 0, md: 8 }}
            width="full"
            maxWidth="full"
            marginTop="9px"
            px="2"
          >
            <GridItem area="menuNav" alignItems="end" borderBottom="1px solid" borderColor="smBorder" width="full" maxWidth="full" />
            <GridItem area="contentNav" alignItems="end" borderBottom="1px solid" borderColor="smBorder" overflow="hidden">
              <EventHeader title={event.eventData.title} modifiedAt={event.modifiedAt} />
            </GridItem>
            <GridItem area="menu" display={{ base: "none", md: "block" }}>
              <Box pt="10">
                <PublicEventNavigation
                  navigateToInfo={navigateToInfo}
                  navigateToProcedure={navigateToProcedure}
                  navigateToQualification={navigateToQualification}
                  navigateToSummary={navigateToSummary}
                />
              </Box>
            </GridItem>
            <GridItem area="content" maxWidth="full" overflowX="hidden">
              <Box maxWidth="full">
                <Box display={currentStep === "info" ? undefined : "none"}>
                  <InfoStep eventId={eventId} event={event} nextStep={navigateToProcedure} />
                </Box>
                <Box display={currentStep === "procedure" ? undefined : "none"}>
                  <ProcedureStep eventId={eventId} event={event} previousStep={navigateToInfo} nextStep={navigateToQualification} />
                </Box>
                <Box display={currentStep === "qualification" ? undefined : "none"}>
                  <QualificationStep
                    eventId={eventId}
                    event={event}
                    previousStep={navigateToProcedure}
                    nextStep={navigateToSummary}
                    isActive={currentStep === "qualification"}
                  />
                </Box>
                <Box display={currentStep === "summary" ? undefined : "none"}>
                  <SummaryStep eventId={eventId} event={event} previousStep={navigateToQualification} navigateToInfo={navigateToInfo} navigateToProcedure={navigateToProcedure} isActive={currentStep === "summary"} />
                </Box>
              </Box>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Layout>
  );
};
